"use client";

import { useEffect, useState } from "react";

import { tags_dictionary } from "@/components/listing-characteristics/dictionaries";
import { supabase } from "@/lib/supabaseClient";

/**
 * Las características de cada ficha para la nube de la tarjeta.
 *
 * Algolia solo indexa las ocho que sirven de filtro. Las demás están en
 * `listing_publico.tags_es`, y se leen de una vez y no tarjeta a tarjeta: son unos
 * 115 KB para toda la cartera (mucho menos comprimidos) y así el globo del mapa
 * también las tiene al momento.
 */

/** Primero lo que más pesa al decidir una visita; el resto, por el orden del diccionario. */
const IMPORTANCIA = [
  "piscina",
  "piscina comunitaria",
  "jardín",
  "jardín comunitario",
  "terraza",
  "vistas al mar",
  "a estrenar",
  "obra nueva",
  "reformado",
  "plaza garaje incluida",
  "parking",
  "edificio con garaje",
  "ascensor",
  "balcón",
  "trastero incluido",
  "trastero",
  "aire acondicionado",
  "amueblado",
];

const puesto = (clave: string) => {
  const i = IMPORTANCIA.indexOf(clave);
  return i === -1 ? IMPORTANCIA.length : i;
};

const POR_CLAVE = new Map(tags_dictionary.map((entrada) => [entrada!.key.toLowerCase(), entrada!]));

/**
 * Solo las del diccionario de la ficha, que es lo que ya se publica: `tags_es`
 * también lleva notas internas. Sin mayúsculas, porque la misma etiqueta llega como
 * «Terraza» y como «terraza».
 */
function etiquetasPublicas(tags: unknown, locale: string): string[] {
  const lista = Array.isArray(tags) ? tags : ((tags as { tags?: unknown[] } | null)?.tags ?? []);
  const claves = new Set(
    lista.filter((t): t is string => typeof t === "string").map((t) => t.trim().toLowerCase()),
  );
  return [...claves]
    .filter((clave) => POR_CLAVE.has(clave))
    .sort((a, b) => puesto(a) - puesto(b))
    .map((clave) => {
      const entrada = POR_CLAVE.get(clave)!;
      return locale === "es" ? entrada.tag : entrada.tagEN;
    });
}

/** Una sola lectura por visita, aunque haya una tarjeta por resultado. */
let lectura: Promise<Map<string, unknown>> | null = null;

function leerEtiquetas() {
  lectura ??= (async () => {
    const { data, error } = await supabase
      .from("listing_publico")
      .select("reference_code, tags_es")
      .not("website", "is", null)
      .neq("website", "enabled")
      .eq("listing_status", "available")
      .limit(5000);
    if (error) throw error;
    return new Map((data ?? []).map((fila) => [fila.reference_code ?? "", fila.tags_es as unknown]));
  })().catch((error) => {
    lectura = null; // que lo vuelva a intentar la siguiente tarjeta
    throw error;
  });
  return lectura;
}

/**
 * Las características de una ficha, de más a menos importantes. Mientras llegan, o
 * si la lectura falla, se queda con las de Algolia (`deReserva`).
 */
export function useEtiquetasDeFicha(
  referencia: string,
  deReserva: string[],
  locale: string,
  yaLeidas?: unknown,
): string[] {
  const [todas, setTodas] = useState<string[] | null>(null);
  const hayQueLeer = yaLeidas === undefined;

  useEffect(() => {
    if (!hayQueLeer) return;
    let cancelada = false;
    leerEtiquetas()
      .then((porFicha) => {
        if (!cancelada && porFicha.has(referencia)) setTodas(etiquetasPublicas(porFicha.get(referencia), locale));
      })
      .catch(() => {});
    return () => {
      cancelada = true;
    };
  }, [referencia, locale, hayQueLeer]);

  return hayQueLeer ? (todas ?? deReserva) : etiquetasPublicas(yaLeidas, locale);
}
